// Eraser: drag over pen strokes to delete them (whole strokes, like Miro's
// eraser; other objects are untouched). One drag is one undo step.
import type { Editor } from '../editor.ts';
import { toLocal } from '../util/geometry.ts';
import { hitStroke } from '../engine/freehand.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const RADIUS_PX = 8;

export class EraserTool implements Tool {
  cursor = 'none';
  private readonly ed: Editor;
  private erasing = false;
  private at: [number, number] | null = null;

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    this.erasing = true;
    this.ed.beginGesture();
    this.eraseAt([[e.x, e.y]]);
  }

  onPointerMove(e: WorldPointerEvent): void {
    this.at = [e.x, e.y];
    if (this.erasing) this.eraseAt(e.coalesced.length ? e.coalesced : [[e.x, e.y]]);
    this.ed.renderer.invalidateInteractive();
  }

  onPointerUp(): void {
    if (!this.erasing) return;
    this.erasing = false;
    this.ed.endGesture();
  }

  onCancel(): void {
    this.onPointerUp();
    this.at = null;
    this.ed.renderer.invalidateInteractive();
  }

  private eraseAt(points: readonly [number, number][]): void {
    const ed = this.ed;
    const r = RADIUS_PX / ed.camera.zoom;
    const hits = new Set<string>();
    for (const [x, y] of points) {
      for (const id of ed.index.query({ x: x - r, y: y - r, w: 2 * r, h: 2 * r })) {
        const o = ed.store.get(id);
        if (o?.type !== 'freehand' || hits.has(id)) continue;
        const [lx, ly] = toLocal(o, x, y);
        if (hitStroke(o, lx, ly, r)) hits.add(id);
      }
    }
    if (hits.size) ed.apply([...hits].map((id) => ({ t: 'delete', id })));
  }

  drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (!this.at) return;
    const zoom = this.ed.camera.zoom;
    ctx.beginPath();
    ctx.arc(this.at[0], this.at[1], RADIUS_PX / zoom, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.fill();
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 1 / zoom;
    ctx.stroke();
  }
}
